import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Organizer, getOrganizerByUserId, getMatchesForOrganizer } from '../../services/dataService';
import { Match } from '../../services/matchingService';
import { Button } from '../../components/Button';
import { ProfileSummary } from '../../components/dashboard/ProfileSummary';
import { MiniMetric } from '../../components/dashboard/MiniMetric';
import { MatchCard } from '../../components/dashboard/MatchCard';
import { ActivityItem } from '../../components/dashboard/ActivityFeed';
export function OrganizerDashboard() {
  const {
    currentUser
  } = useAuth();
  const [organizer, setOrganizer] = useState<Organizer | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadData = async () => {
      if (currentUser) {
        const organizerData = getOrganizerByUserId(currentUser.id);
        setOrganizer(organizerData);
        if (organizerData) {
          const matchData = getMatchesForOrganizer(organizerData.id);
          setMatches(matchData);
        }
      }
      setLoading(false);
    };
    loadData();
  }, [currentUser]);
  if (loading) {
    return <DashboardLayout userType="organizer" data-id="element-2416">
        <div className="flex justify-center items-center h-64" data-id="element-2417">
          <div className="text-gray-500" data-id="element-2418">Loading...</div>
        </div>
      </DashboardLayout>;
  }
  if (!organizer) {
    return <DashboardLayout userType="organizer" data-id="element-2419">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-2420">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2421">
            Complete Your Profile
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-2422">
            You need to complete your event organizer profile before you can
            start matching with brands.
          </p>
          <Link to="/onboarding/organizer" data-id="element-2423">
            <Button variant="primary" data-id="element-2424">Complete Profile</Button>
          </Link>
        </div>
      </DashboardLayout>;
  }
  // Calculate stats
  const pendingMatches = matches.filter(m => m.status === 'pending');
  const acceptedMatches = matches.filter(m => m.status === 'accepted');
  const avgMatchScore = matches.length > 0 ? Math.round(matches.reduce((acc, m) => acc + m.score, 0) / matches.length) : 0;
  return <DashboardLayout userType="organizer" data-id="element-2425">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2426">
        {/* Left sidebar */}
        <aside className="lg:col-span-1 bg-white rounded-2xl p-4 shadow-sm h-fit" data-id="element-2427">
          <ProfileSummary view="organizer" name={organizer.eventName} stats={{
          matches: matches.length,
          openDeals: pendingMatches.length,
          activeCampaigns: acceptedMatches.length,
          avgMatch: `${avgMatchScore}%`
        }} data-id="element-2428" />
        </aside>

        {/* Main content */}
        <section className="lg:col-span-3 space-y-6" data-id="element-2429">
          {/* Event Overview */}
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-2430">
            <div className="flex items-start justify-between" data-id="element-2431">
              <div data-id="element-2432">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-2433">
                  Event Overview
                </h2>
                <p className="text-sm text-gray-500" data-id="element-2434">
                  Manage sponsor requests, activate offers, and review candidate
                  brands.
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-2435">
                <Link to="/onboarding/organizer" className="text-sm px-3 py-1 border rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2436">
                  Edit event
                </Link>
                <Link to="/dashboard/matches" className="text-sm px-3 py-1 bg-violet-600 text-white rounded-md hover:bg-violet-700" data-id="element-2437">
                  Find Sponsors
                </Link>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2438">
              <MiniMetric title="Attendance" value={organizer.attendeeCount === 'under_100' ? '< 100' : organizer.attendeeCount === '100_500' ? '100 - 500' : organizer.attendeeCount === '500_1000' ? '500 - 1k' : organizer.attendeeCount === '1000_5000' ? '1k - 5k' : '5k+'} data-id="element-2439" />
              <MiniMetric title="Avg. Match" value={`${avgMatchScore}%`} data-id="element-2440" />
              <MiniMetric title="Event Type" value={organizer.eventType ? organizer.eventType.charAt(0).toUpperCase() + organizer.eventType.slice(1).replace('_', ' ') : 'General'} data-id="element-2441" />
            </div>
          </section>

          {/* Top Matching Brands */}
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-2442">
            <div className="flex items-center justify-between mb-4" data-id="element-2443">
              <div data-id="element-2444">
                <h3 className="text-md font-semibold text-gray-900" data-id="element-2445">
                  Top Matching Brands
                </h3>
                <p className="text-sm text-gray-500" data-id="element-2446">
                  Based on your audience & needs
                </p>
              </div>
              <Link to="/dashboard/matches" className="text-sm text-violet-600 hover:text-violet-800 font-medium" data-id="element-2447">
                View all
              </Link>
            </div>
            <div className="space-y-3" data-id="element-2448">
              {matches.length === 0 ? <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200" data-id="element-2449">
                  No matches found yet. Complete your profile to get matched
                  with relevant brands.
                </div> : matches.slice(0, 3).map(match => <MatchRowWrapper key={match.id} match={match} data-id="element-2450" />)}
            </div>
          </section>

          {/* Sponsor Requests */}
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-2451">
            <div className="flex items-center justify-between mb-3" data-id="element-2452">
              <h3 className="text-md font-semibold text-gray-900" data-id="element-2453">
                Sponsor Requests
              </h3>
              <div className="text-sm text-gray-500" data-id="element-2454">Incoming proposals</div>
            </div>
            <div className="space-y-3" data-id="element-2455">
              {pendingMatches.length === 0 ? <div className="text-sm text-gray-500 italic" data-id="element-2456">
                  No pending requests.
                </div> : pendingMatches.slice(0, 2).map(match => <ActivityItemWrapper key={`request-${match.id}`} match={match} data-id="element-2457" />)}
            </div>
          </section>
        </section>
      </div>
    </DashboardLayout>;
}
// Wrapper components
function MatchRowWrapper({
  match
}: {
  match: Match;
}) {
  const [brand, setBrand] = useState<any>(null);
  useEffect(() => {
    const brandData = JSON.parse(localStorage.getItem('brands') || '[]');
    const found = brandData.find((b: any) => b.id === match.brandId);
    setBrand(found || null);
  }, [match]);
  if (!brand) return null;
  return <MatchCard id={match.id} name={brand.companyName} sub={brand.productName} desc={brand.description || brand.productDescription || 'No description available'} matchScore={match.score} status={match.status} view="organizer" data-id="element-2458" />;
}
function ActivityItemWrapper({
  match
}: {
  match: Match;
}) {
  const [brand, setBrand] = useState<any>(null);
  useEffect(() => {
    const brandData = JSON.parse(localStorage.getItem('brands') || '[]');
    const found = brandData.find((b: any) => b.id === match.brandId);
    setBrand(found || null);
  }, [match]);
  if (!brand) return null;
  return <ActivityItem title={`Proposal from ${brand.companyName}`} subtitle={`Budget: ${brand.budget || 'Negotiable'}`} status="Pending" type="request" data-id="element-2459" />;
}